import { Config } from '../video-data/config';
import { computed, observable } from '../core/reactivity';
import { assertNumber } from '../validators';
import { BaseTimeline } from './BaseTimeline';

/**
 *
 * A type of timeline that is automatically placed in sequence under a parent.
 *
 * The relativeStartTime is automatically calculated and cannot be set directly. It's possible
 * to shift a SequentialTimeline from its default position by using the offset property.
 * Note that the offset cannot position a timeline to start before its parent.
 *
 * Changing the position or duration of a SequentialTimeline will result in any following
 * siblings automatically getting updated.
 */
export class SequentialTimeline extends BaseTimeline {
    override _classType = Config.SEQUENTIAL_TIMELINE_TYPE;
    @observable accessor #offset = 0;

    override _moveFromTo(parent: BaseTimeline, oldIndex: number, newIndex: number) {
        super._moveFromTo(parent, oldIndex, newIndex);

        // the placements recalculate themselves; this is the change notification the shifted sibling
        // owes consumers outside this repository, and retires with the update flag itself (LUM-663)
        const updateIndex = Math.min(oldIndex, newIndex);
        parent._children[updateIndex].requiresUpdate = true;
    }

    override _removeFromParent(parent: BaseTimeline, index: number) {
        super._removeFromParent(parent, index);

        // as in `_moveFromTo`: notification for the sibling that shifted, not a recalculation
        if (parent._children.length > index) {
            parent._children[index].requiresUpdate = true;
        }
    }

    /** @internal */
    @computed
    override get _relativeStartTime(): number {
        const parent = this.parent;
        if (!parent) {
            return 0;
        }

        const index = parent._children.indexOf(this);
        const previous = index > 0 ? parent._children[index - 1] : undefined;

        // a timeline cannot start before its parent
        return Math.max(this.#offset + (previous?.relativeEndTime ?? 0), 0);
    }

    /**
     * A positive/negative amount to offset this timeline by.
     *
     * Overlapping timelines can be created by setting a negative offset.
     * ----------------------------------------------------------------------
     * |[_ parent timeline ________________________________________________]|
     * |[_ sequential timeline #1 __________________]                       |
     * |                           <~~~ - offset ~~~>                       |
     * |                           [_ sequential timeline #2 ______________]|
     * ----------------------------------------------------------------------
     *
     * Timelines with empty gaps can be created by setting a positive offset.
     * ------------------------------------------------------==----------------
     * |[_ parent timeline __________________________________________________]|
     * |[_ sequential timeline #1 _]                                          |
     * |                            <~ + offset ~>                            |
     * |                                          [_ sequential timeline #2 _]|
     * ------------------------------------------------------------------------
     */
    get offset(): number {
        return this.#offset;
    }

    set offset(value: number) {
        if (value === this.#offset) {
            return;
        }
        assertNumber(value);
        this.#offset = value;
        this.requiresUpdate = true;
    }
}
